import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useHistory } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Flex,
  Label,
  Table,
  Button,
  Tooltip,
  TableRow,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  Pagination,
  FilterMenu,
  PageWrapper,
  StretchPanel,
  OrderableTableRow,
  useFilterReducer,
  usePaginationReducer,
  useOrderableTableReducer,
} from "@sopranodesign/pulse-components";
import Icon from "@sopranodesign/pulse-icons";

dayjs.extend(relativeTime);

const FeaturesLogs = ({
  location: {
    state: { project, account },
  },
}) => {
  const history = useHistory();

  const pagination = usePaginationReducer({ id: "features-logs" });

  const order = useOrderableTableReducer({ id: "features-logs" });

  const filters = useFilterReducer({
    id: "features-logs",
    initialFilters: { email: "" },
    options: { pagination },
  });

  const queryFeature = useQuery({
    queryKey: [
      account && account.id
        ? `features/${project.id}/${account.id}`
        : `features/${project.id}/install`,
    ],
  });

  const queryLogs = useQuery({
    queryKey: [
      account && account.id
        ? `features/${project.id}/${account.id}/logs`
        : `features/${project.id}/install/logs`,
      {
        ...pagination.params,
        ...order.params,
        ...filters.form,
      },
    ],
  });

  const handleSearchChange = (e) => {
    filters.updateFilters({ email: e.target.value });
  };

  return (
    <PageWrapper
      header="Features Logs"
      subheader={`View feature logs for ${
        project && project.name ? project.name : "Your Project"
      } - ${account && account.name ? account.name : "All Install"}`}
      actions={
        <Flex xSpace>
          <FilterMenu
            buttonProps={{ loading: queryLogs.isLoading }}
            {...filters.props}
          >
            <Flex style={{ width: "20rem" }} col>
              <Flex col fat mb>
                <Label htmlFor="email">User Email</Label>
                <TextInput
                  id="email"
                  placeholder="Search by user email"
                  value={filters.filters.email}
                  onChange={handleSearchChange}
                />
              </Flex>
            </Flex>
          </FilterMenu>
          <Button
            icon="arrowNarrowLeft"
            variant="primary"
            onClick={() => history.goBack()}
          >
            Back
          </Button>
        </Flex>
      }
    >
      {queryLogs.isError && (
        <NoResults
          text="There was a problem loading feature logs"
          subtitle="Double check the projects credentials or try again later"
        />
      )}

      {queryLogs.isSuccess && queryLogs.data.data.length === 0 && (
        <NoResults
          text="No feature logs found"
          subtitle="Feature activity will appear here once features are used"
        />
      )}

      {queryLogs.isSuccess && queryLogs.data.data.length > 0 && (
        <StretchPanel>
          <Table loading={queryLogs.isLoading} loadingColumns={4}>
            <TableHead>
              <OrderableTableRow
                rows={[
                  { label: "User", sort: "users.email" },
                  { label: "Feature", sort: null },
                  { label: "Request", sort: null },
                  { label: "When", sort: "created_at" },
                ]}
                {...order.props}
              />
            </TableHead>
            {!queryLogs.isLoading && (
              <TableBody>
                {queryLogs.data.data.map((log, key) => (
                  <TableRow key={key}>
                    <TableCell>
                      <div>{log.userEmail}</div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {queryFeature.isSuccess &&
                        queryFeature.data.data.filter(
                          (feature) => feature.id === log.featureId,
                        ).length > 0 ? (
                          queryFeature.data.data.filter(
                            (feature) => feature.id === log.featureId,
                          )[0]["name"]
                        ) : (
                          <div>
                            {log.featureId}
                            <Tooltip
                              position={Tooltip.POSITIONS.RIGHT}
                              tooltip={
                                <Tooltip.Content>
                                  Unable to featch the name, perhaps the feature
                                  name is removed
                                </Tooltip.Content>
                              }
                            >
                              <Icon
                                stroke="red"
                                size="1rem"
                                width="1rem"
                                height="1rem"
                                type="exclamation"
                                outline
                                style={{ cursor: "auto" }}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{log.request ? "activated" : "deactivated"}</div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {dayjs(log.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {queryLogs.showPagination && (
            <Pagination
              pagination={queryLogs.data.meta}
              {...pagination.props}
            />
          )}
        </StretchPanel>
      )}
    </PageWrapper>
  );
};

export default FeaturesLogs;
